import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

const button = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'rounded-full font-medium whitespace-nowrap',
    // Colour *and* transform, so a press has physical feedback. The scale is
    // 2% and lasts 120ms — enough to feel like the control acknowledged the
    // tap on a phone, where there is no hover state to do that job, and small
    // enough that nothing visibly reflows. Transform-only, so it composites.
    'transition-[color,background-color,border-color,transform] duration-[--hs-duration-fast] ease-out',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-busy:cursor-progress',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-button-primary text-button-primary-fg hover:bg-button-primary-hover',
        secondary:
          'bg-button-secondary text-text border border-button-secondary-border hover:bg-button-secondary-hover',
        /**
         * The site's one standing call to action ("Start a project" /
         * "Work With Bitwize") — the reference's gold pill (bitwize.html,
         * 2026-09), used wherever that specific button appears regardless
         * of the surface under it. Deliberately not the default `primary`:
         * every other button on the site keeps the approved blue.
         */
        gold: 'bg-button-gold text-button-gold-fg hover:bg-button-gold-hover',
        ghost: 'bg-transparent text-text hover:bg-surface-hover',
        danger: 'bg-danger text-accent-fg hover:bg-danger-hover',
        link: 'bg-transparent text-accent underline underline-offset-4 hover:text-accent-hover',
      },
      size: {
        // `sm` is below the 44px touch target and is for dense admin tables
        // only — never for a public interactive element.
        sm: 'h-8 px-3 text-sm',
        md: 'h-11 px-4 text-base',
        lg: 'h-13 px-6 text-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
);

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>, VariantProps<typeof button> {
  className?: string;
  loading?: boolean;
  /** Rendered at the inline start — the left in LTR, the right in RTL. */
  iconStart?: ReactNode;
  /** Rendered at the inline end. */
  iconEnd?: ReactNode;
  /** Render as the single child element instead of a <button>. */
  asChild?: boolean;
}

/**
 * The system's button.
 *
 * `loading` keeps the label rather than replacing it: swapping the text for a
 * spinner changes the button's width mid-click and moves whatever is next to
 * it. It also sets `aria-busy` and blocks activation, so the visual and the
 * assistive-technology states agree.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    fullWidth,
    loading = false,
    disabled,
    iconStart,
    iconEnd,
    asChild = false,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  const Component = asChild ? Slot : 'button';

  return (
    <Component
      ref={ref}
      // `type` defaults to "submit" inside a form, which is how a "Cancel"
      // button ends up submitting the form it was meant to abandon.
      type={asChild ? undefined : type}
      className={cn(button({ variant, size, fullWidth }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : iconStart}
      {/*
        Slottable marks which child Slot should merge into. Without it, an
        `asChild` button with an icon hands Slot three children and it throws —
        which is a server-render failure, not a visual glitch.
      */}
      <Slottable>{children}</Slottable>
      {iconEnd}
    </Component>
  );
});
